import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, ArrowRight, FileText, Download, CalendarDays } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ORDER_STEPS } from "@/data/services";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const STATUS_COLORS = {
  awaiting_payment: "bg-slate-500/15 text-slate-400 border-slate-600/40",
  received: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  questionnaire_received: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  hr_review: "bg-violet-500/15 text-violet-400 border-violet-500/30",
  draft_ready: "bg-amber-500/15 text-amber-300 border-amber-400/40",
  completed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    if (user === false) return navigate("/auth?next=/dashboard");
    if (!user) return;
    if (user.role === "admin") return navigate("/admin");
    api.get("/orders/my").then((r) => setOrders(r.data)).catch(() => setOrders([]));
    api.get("/bookings/my").then((r) => setBookings(r.data)).catch(() => {});
  }, [user, navigate]);

  const downloadDoc = async (orderId, filename) => {
    try {
      const res = await api.get(`/orders/${orderId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "RMS-Group-Documents.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // surfaced by caller UI state
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  if (user === null || orders === null) {
    return <div className="mx-auto max-w-5xl px-5 py-32 text-sm text-slate-500" data-testid="dashboard-loading">Loading your portal…</div>;
  }

  return (
    <div className="mx-auto max-w-5xl px-5 pb-24 pt-16 lg:px-8 lg:pt-24" data-testid="client-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <span className="overline-label">Client Portal</span>
          <h1 className="mt-3 font-serif text-4xl font-semibold text-slate-50" data-testid="dashboard-greeting">
            Welcome, {user.name.split(" ")[0]}
          </h1>
          <p className="mt-2 text-sm text-slate-400">{user.email}</p>
        </div>
        <div className="flex gap-3">
          <Link to="/order" className="btn-gold !px-5 !py-2.5" data-testid="dashboard-new-order-btn">New Order</Link>
          <button onClick={doLogout} className="btn-ghost !px-5 !py-2.5" data-testid="dashboard-logout-btn">
            <LogOut size={15} /> Sign Out
          </button>
        </div>
      </div>

      {orders.length === 0 ? (
        <Reveal>
          <div className="card-surface mt-12 p-12 text-center" data-testid="dashboard-empty">
            <FileText size={36} className="mx-auto text-[#C5A059]" />
            <h2 className="mt-6 font-serif text-2xl font-semibold text-slate-50">No orders yet</h2>
            <p className="mx-auto mt-3 max-w-sm text-sm text-slate-400">Your UK career documents start here — pick a service and your order tracker will appear.</p>
            <Link to="/services" className="btn-gold mt-8" data-testid="dashboard-browse-services-btn">Browse Services <ArrowRight size={15} /></Link>
          </div>
        </Reveal>
      ) : (
        <div className="mt-12 space-y-8">
          {orders.map((o, idx) => {
            const stepIdx = ORDER_STEPS.findIndex((s) => s.id === o.status);
            return (
              <Reveal key={o.id} delay={idx * 0.06}>
                <div className="card-surface p-8" data-testid={`dashboard-order-${o.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <h2 className="font-serif text-2xl font-semibold text-slate-50">{o.package_name}</h2>
                      <p className="mt-1 font-mono text-xs text-slate-500">
                        ORDER {o.id.slice(0, 8).toUpperCase()} · £{Number(o.amount).toFixed(2)} GBP · {new Date(o.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                      </p>
                      {o.delivery_days && (
                        <p className="mt-1 font-mono text-xs text-[#C5A059]" data-testid={`dashboard-delivery-${o.id}`}>
                          {o.delivery_speed === "advanced" ? "ADVANCED PRIORITY" : "BASIC DELIVERY"} · {o.delivery_days}
                        </p>
                      )}
                    </div>
                    <span className={`rounded-full border px-4 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] ${STATUS_COLORS[o.status] || STATUS_COLORS.received}`} data-testid="client-order-status-badge">
                      {ORDER_STEPS[stepIdx]?.label || o.status}
                    </span>
                  </div>

                  {/* PROGRESS TRACKER */}
                  <div className="mt-8 flex items-center" data-testid="order-progress-tracker">
                    {ORDER_STEPS.map((s, i) => {
                      if (s.id === "awaiting_payment" && o.status !== "awaiting_payment") return null;
                      const active = i <= stepIdx;
                      return (
                        <div key={s.id} className="flex flex-1 items-center last:flex-none">
                          <div className="flex flex-col items-center gap-2">
                            <span className={`h-3 w-3 rounded-full border-2 transition-colors duration-500 ${active ? "border-[#C5A059] bg-[#C5A059]" : "border-slate-700 bg-transparent"}`} />
                            <span className={`hidden whitespace-nowrap font-mono text-[9px] uppercase tracking-[0.1em] sm:block ${active ? "text-slate-300" : "text-slate-600"}`}>{s.label}</span>
                          </div>
                          {i < ORDER_STEPS.length - 1 && <div className={`mx-2 h-px flex-1 ${i < stepIdx ? "bg-[#C5A059]" : "bg-slate-800"}`} />}
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-8 flex flex-wrap items-center gap-4 border-t border-slate-800 pt-6">
                    {o.status === "awaiting_payment" ? (
                      <Link to={`/order?package=${o.package_id}`} className="btn-gold !px-5 !py-2.5" data-testid={`dashboard-complete-payment-${o.id}`}>
                        Complete Payment <ArrowRight size={14} />
                      </Link>
                    ) : !o.intake ? (
                      <Link to={`/intake?order_id=${o.id}`} className="btn-gold !px-5 !py-2.5" data-testid={`dashboard-complete-intake-${o.id}`}>
                        Complete Questionnaire <ArrowRight size={14} />
                      </Link>
                    ) : (
                      <span className="text-sm text-slate-400" data-testid={`dashboard-intake-done-${o.id}`}>
                        Brief submitted {o.intake.cv_file ? `· CV: ${o.intake.cv_file}` : ""} — our HR desk is on it.
                      </span>
                    )}
                    {["draft_ready", "completed"].includes(o.status) && o.intake && (
                      <button
                        onClick={() => downloadDoc(o.id, o.intake?.cv_file ? `RMS-${o.package_name}.pdf` : undefined)}
                        className="btn-gold !px-5 !py-2.5"
                        data-testid={`dashboard-download-${o.id}`}
                      >
                        <Download size={14} /> Download Documents
                      </button>
                    )}
                    {o.status === "completed" && (
                      <span className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-300" data-testid={`dashboard-complete-notice-${o.id}`}>
                        Delivered. Need a revision? Contact us and we'll refine it.
                      </span>
                    )}
                    {o.package_id.includes("interview") && o.status !== "awaiting_payment" && (
                      bookings.find((b) => b.order_id === o.id) ? (
                        <span className="inline-flex items-center gap-2 rounded-lg border border-[#C5A059]/40 bg-[#C5A059]/10 px-4 py-2 text-sm text-[#C5A059]" data-testid={`dashboard-booking-confirmed-${o.id}`}>
                          <CalendarDays size={14} />
                          Session: {new Date(bookings.find((b) => b.order_id === o.id).date + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "long" })} at {bookings.find((b) => b.order_id === o.id).time} GMT
                        </span>
                      ) : (
                        <Link to={`/booking?order_id=${o.id}`} className="btn-ghost !px-5 !py-2.5" data-testid={`dashboard-book-session-${o.id}`}>
                          <CalendarDays size={14} /> Book Your Session
                        </Link>
                      )
                    )}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      )}
    </div>
  );
}
